/**
 * Citations Contract — The typed API contract for the Citations Intelligence domain.
 *
 * Citations are AI-enriched references with strength scoring,
 * linking, metadata completeness checking, and duplicate detection.
 *
 * BACKEND ALIGNMENT:
 * - Follows RFC 9457 ProblemDetail for errors
 * - Supports cursor + offset pagination with HATEOAS links
 * - All timestamps are ISO 8601 strings
 *
 * MIGRATION NOTE:
 * Zod schemas are re-exported from auto-generated code (@hey-api/openapi-ts).
 * Types inferred from generated schemas. UI-only constructs remain hand-written.
 */

import { z } from 'zod';
import type { PaginatedResponse, PaginationParams, SortParams, ApiResponse } from './shared';

// Re-export generated schemas
import {
  zCitationType as GeneratedCitationType,
  zCitationStrength as GeneratedCitationStrength,
  zCitationStatus as GeneratedCitationStatus,
  zCitationRead as GeneratedCitationRead,
  zCitationMetricsRead as GeneratedCitationMetricsRead,
  zAiSuggestionRead as GeneratedAiSuggestionRead,
  zDuplicateAlertRead as GeneratedDuplicateAlertRead,
} from '@/api/generated/hey-api/zod.gen';

// ─── Enums ─────────────────────────────────────────────────────────────

export const CitationTypeEnum = GeneratedCitationType;
export type CitationType = z.infer<typeof CitationTypeEnum>;

export const CitationStrengthEnum = GeneratedCitationStrength;
export type CitationStrength = z.infer<typeof CitationStrengthEnum>;

export const CitationStatusEnum = GeneratedCitationStatus;
export type CitationStatus = z.infer<typeof CitationStatusEnum>;

// ─── DTOs ──────────────────────────────────────────────────────────────

export const CitationDTOSchema = GeneratedCitationRead;
export type CitationDTO = z.infer<typeof CitationDTOSchema>;

export const CitationMetricsDTOSchema = GeneratedCitationMetricsRead;
export type CitationMetricsDTO = z.infer<typeof CitationMetricsDTOSchema>;

export const AISuggestionDTOSchema = GeneratedAiSuggestionRead;
export type AISuggestionDTO = z.infer<typeof AISuggestionDTOSchema>;

export const DuplicateAlertDTOSchema = GeneratedDuplicateAlertRead;
export type DuplicateAlertDTO = z.infer<typeof DuplicateAlertDTOSchema>;

// ─── Request Types ─────────────────────────────────────────────────────
// UI-only: Request interfaces for frontend query/mutation composition.

export interface CitationListFilters {
  status?: CitationStatus[];
  strength?: CitationStrength[];
  type?: CitationType[];
  tags?: string[];
  aiSuggested?: boolean;
  hasMissingMetadata?: boolean;
  search?: string;
}

export type CitationSortField = 'title' | 'year' | 'confidenceScore' | 'createdAt' | 'strength';

export interface CitationListParams
  extends PaginationParams, SortParams<CitationSortField>, CitationListFilters {}

export interface CreateCitationRequest {
  type: CitationType;
  title: string;
  authors: string[];
  year: number;
  journal?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  doi?: string;
  url?: string;
  abstract?: string;
  tags?: string[];
}

export interface UpdateCitationRequest {
  title?: string;
  authors?: string[];
  year?: number;
  journal?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  doi?: string;
  url?: string;
  abstract?: string;
  tags?: string[];
  status?: CitationStatus;
}

// ─── Response Types ────────────────────────────────────────────────────
// UI-only: Response type aliases for frontend convenience.

export type CitationListResponse = PaginatedResponse<CitationDTO>;
export type CitationDetailResponse = ApiResponse<CitationDTO>;
export type CreateCitationResponse = ApiResponse<CitationDTO>;
export type UpdateCitationResponse = ApiResponse<CitationDTO>;
export type DeleteCitationResponse = ApiResponse<{ id: string; deleted: boolean }>;
export type CitationMetricsResponse = ApiResponse<CitationMetricsDTO>;
export type AISuggestionsResponse = ApiResponse<AISuggestionDTO[]>;
export type DuplicateAlertsResponse = ApiResponse<DuplicateAlertDTO[]>;

// ─── Export Types ──────────────────────────────────────────────────────

export type CitationExportFormat = 'bibtex' | 'ris' | 'csl-json';

export interface CitationExportParams {
  format: CitationExportFormat;
  /** Specific citation IDs to export. Exports all if omitted. */
  ids?: string[];
}

// ─── Adapter Interface ─────────────────────────────────────────────────
// UI-only: Adapter interface for dependency inversion (mock/HTTP).

export interface ICitationsAdapter {
  list(params: CitationListParams): Promise<CitationListResponse>;
  getById(id: string): Promise<CitationDetailResponse>;
  create(data: CreateCitationRequest): Promise<CreateCitationResponse>;
  update(id: string, data: UpdateCitationRequest): Promise<UpdateCitationResponse>;
  delete(id: string): Promise<DeleteCitationResponse>;
  getMetrics(): Promise<CitationMetricsResponse>;
  getAISuggestions(): Promise<AISuggestionsResponse>;
  getDuplicateAlerts(): Promise<DuplicateAlertsResponse>;
  exportCitations(params: CitationExportParams): Promise<Blob>;
}
